import helpers from "../helpers";
import { _uniqueName } from "./utils";
import { Notification } from "./Notification";
import { action, computed, makeObservable, observable } from "mobx";

export function ApplicationParameters(pApplication, pApplicationData) {
  Notification.call(this);
  this.mApp = pApplication;
  // it is a hashed of the parameters
  // the parameters name is the hash key
  this.mParameters = {};
  this.fromJson(pApplicationData);

  makeObservable(this, {
    mParameters: observable,
    parameters: computed,
    json: computed,
    addParameter: action,
    fromJson: action,
    updateParameter: action,
    removeParameter: action,
  });
}

ApplicationParameters.prototype = Object.create(Notification.prototype);
ApplicationParameters.prototype.constructor = ApplicationParameters;
ApplicationParameters.prototype.isValidParameter = function (value) { return !isNaN(value) || this.isNameUsed(value) ? undefined : (value + ' is not a valid variable name'); };
/**
 * Add a new application parameter with a default name
 * It updates the application payload and
 * send it notifies the application with the
 * onApplicationParameterAdded
 * @description this function is overridden in vention_assembler
 */
ApplicationParameters.prototype.addParameter = function(pType) {
  let lId; let lName; const lConfigManager = this.mApp.mLibrary.getConfigManager();
  if (pType == 'const') {
    lName = this.uniqueParameterName(this.getDefaultName());
    lId = Object.keys(this.mParameters).length + '_' + lName;
    const lDefaultValue = '100';
    this.mParameters[lId] = { id: lId, name: lName, default_value: lDefaultValue, type: 'const' };
  } else if (pType == 'lambda') {
    lName = this.uniqueParameterName(this.getDefaultName(pType));
    lId = Object.keys(this.mParameters).length + '_' + lName;
    const default_arguments = 'a,b';
    const default_function = 'return a+b;';
    this.mParameters[lId] = { id: lId, name: lName, default_value: {default_arguments, default_function}, type: 'lambda' };
  } else if (pType === 'unpackConst') {
    lName = this.uniqueParameterName(this.getDefaultName(pType));
    lId = Object.keys(this.mParameters).length + '_' + lName;
    const default_unpackConst = '{}';
    this.mParameters[lId] = { id: lId, name: lName, default_value: default_unpackConst, type: 'unpackConst' };
  } else {
    // Search for undefined input
    const lInputParams = Object.values(this.mParameters).filter((pParam) => {return pParam.type == 'input';});
    let lInput = lConfigManager.listInputs().find((pInput) => {
      const match = lInputParams.find((pParam) => { return pParam.inputUuid == pInput.getUUID(); });
      if (match == undefined) return true; // input does not exist as variable
    });
    // If all variables are used, default to first variable
    if (lInput == undefined) { lInput = lConfigManager.listInputs()[0]; }
    lName = this.uniqueParameterName(lInput.getName().replace(/[&\/\\#, +()$~%.'":*?<>{}@]/g, '_'));
    lId = Object.keys(this.mParameters).length + '_' + lName;
    this.mParameters[lId] = { id: lId, name: lName, inputUuid: lInput.getUUID(), type: 'input' };
  }
  this.mParameters = Object.assign({}, this.mParameters);
  this.setDirty();
  this.mApp.notify('onApplicationParameterAdded', this.mParameters[lId]);
  return this.mParameters[lId];
};

/**
 * Initialize Application parameters
 */
ApplicationParameters.prototype.fromJson = function (pApplication) {
  this.mParameters = (pApplication.hasOwnProperty('parameters') && pApplication.parameters.reduce(function (acc, curr, i) {
    const id = i + '_' + curr.name;
    acc[id] = Object.assign({id: id }, curr);
    return acc;
  }, {})) || this.mParameters;
};

/**
 * Get the default name for new parameters
 * @returns {String}
 */
ApplicationParameters.prototype.getDefaultName = function (type) {
  if (type == "lambda"){
    return 'myFunction';
  } else if (type == "unpackConst"){
    return 'UNPACK_MESSAGE';
  } else {
    return 'VARIABLE';
  }
};

/**
 * Get the value of an app parameter
 * @returns {Number}
 */
ApplicationParameters.prototype.getParameterValue = function (pParameterName) {
  if (!pParameterName) { console.warn('ApplicationParameters.prototype.getParameter value called without the parameter name argument.'); return; }

  const applicableApplicationParametersList = this.mApp.mParameters.mParameters;

  // Validate that the expression makes sense for javascript (eval)
  // Application parameters declaration
  let appParamDeclaration = "";
  for (const applicationParameterKey in applicableApplicationParametersList) {
    const applicationParameter = applicableApplicationParametersList[applicationParameterKey];
    if (applicationParameter.type == 'const' || applicationParameter.type == 'unpackConst') {
      const value = applicationParameter.default_value;
      if (Array.isArray(value)) {
        appParamDeclaration += `var ${applicationParameter.name} = [ ${value.map(a => `"${a}"`)} ];`;
      } else if (typeof value == 'object') {
        appParamDeclaration += `var ${applicationParameter.name} = ${JSON.stringify(value)};`;
      } else {
        appParamDeclaration += `var ${applicationParameter.name} = ${value};`;
      }
    } else if (applicationParameter.type == 'lambda') {
      appParamDeclaration += `var ${applicationParameter.name} = function(${applicationParameter.default_value.default_arguments}){${applicationParameter.default_value.default_function}};`;
    } else if (applicationParameter.type == 'input') {
      // Note : the type "input", for inputs as variables, has a default value of 1.
      appParamDeclaration += `var ${applicationParameter.name} = 1;`;
    } else {
      // This is not supposed to happen.
      console.warn("ApplicationParameters.prototype.getParameterValue does not support param type: " + applicationParameter.type);
      appParamDeclaration += `var ${applicationParameter.name} = 0;`;
    }
  }
  return eval(appParamDeclaration + pParameterName);
};


/**
 * Checks whether the parameter is valid.
 * @param {string} pParameterName
 * @returns Boolean
 */
ApplicationParameters.prototype.hasParameter = function(pParameterName, pCaseSensitive) {
  if (!pParameterName) { return false; }
  const lParamObj = Object.values(this.mParameters).find(function(pParamObj) { return pParamObj.name === (pCaseSensitive ? pParameterName : console.error('Parameter name is case sensitive')); });
  if (!lParamObj) { return false; }
  return this.mParameters.hasOwnProperty(lParamObj.id);
};

/**
 * Check if a parameter is used on the application
 * @param {String} pParameterName - parameter name to check if is used in the application
 */
ApplicationParameters.prototype.isParameterUsed = function(pParameterName) {
  let isParameterUsed = false;
  const containParameter = function(pParamValue) { return new RegExp(pParameterName + '\\b').test(pParamValue); };

  this.mApp.mSequences.forEach(function (pSequenceObject) {
    pSequenceObject.mInstructions.forEach(function (pInstructionObject) {
      Object.values(pInstructionObject.mParameters).forEach( function(pParameter){
        // The parameter object is an array
        if ( Array.isArray(pParameter) ){
          pParameter.forEach(function (pSubParameter) {
            if (Object.values(pSubParameter).find(containParameter)) {
              isParameterUsed = true;
            }
          });
        }
        // The parameter object is not an array
        else if (containParameter(pParameter)){
          isParameterUsed = true;
        }
      });
    });
  });

  return isParameterUsed;
};


/**
 * Check is the parameter name is already used
 * @param {String} pName - check if the new name is already in use
 * @returns {Boolean}
 */
ApplicationParameters.prototype.isNameUsed = function(pName) {
  const ParametersNameList = this.listParametersName().map(function(x){ return x; });
  return ParametersNameList.includes(pName.split("(")[0]);
};

ApplicationParameters.prototype.isValidVariableName = function(pName) {
  return pName.match(helpers.validJSVariableNameRegex);
};

/**
 * List all parameters name
 * @returns {Array[string]}
 */
ApplicationParameters.prototype.listParametersName = function() {
  // Note (alevy): filter out the variable type referenceFrame
  return Object.values(this.mParameters).filter((pParamObj) => !/referenceFrame/i.test(pParamObj.type)).map(function(pParamObj) { return pParamObj.name; });
};
ApplicationParameters.prototype.listParametersNameOfType = function(pType) {
  return Object.values(this.mParameters).filter(pParamObj => pType.includes(pParamObj.type)).map(function(pParamObj) { return pParamObj.name; });
};

/**
 * Remove an application parameter
 * @param {String} pParameterId - parameter name to be deleted
 * @description this function is overridden in vention_assembler
 */
ApplicationParameters.prototype.removeParameter = function (pParameterId) {
  const lOldParameter = Object.assign({}, this.mParameters[pParameterId]);
  delete this.mParameters[pParameterId];
  this.mParameters = Object.assign({}, this.mParameters);
  this.setDirty();
  this.mApp.notify('onApplicationParameterRemoved', lOldParameter);
};

/**
 * This call the library set dirty
 * so it sends the updated data to
 * the server
 */
ApplicationParameters.prototype.setDirty = function () {
  this.mApp.setDirty();
};

/**
 * Outcome the parameters as an array of parameter object
 * @param pKeepId - if we want to get the full object with the Id we should set it to true
 * @returns {Array[{ name: String, default_value: String, type: String }]}
 */
ApplicationParameters.prototype.toJson = function(pKeepId) {
  return Object.values(this.mParameters).reduce(function(acc, curr) {
    if (pKeepId) {
      acc.push(curr);
    } else {
      if (curr.type == 'const' || curr.type == 'lambda' || curr.type == 'unpackConst' ) {
        acc.push({ default_value: curr.default_value, name: curr.name, type: curr.type });
      } else {
        acc.push({ name: curr.name, inputUuid: curr.inputUuid, type: curr.type });
      }
    }
    return acc;
  }, []);
};

/**
 * Update an application parameter name or default_value
 * @param {String} pParameterId - Parameter name used in the mParameters hash
 * @param {{ name: String, default_value: String }} pPayload - Payload to update the parameter
 * @returns void
 */
ApplicationParameters.prototype.updateParameter = function(pParameterId, pPayload) {
  const lThis = this; const lPayloadKeys = Object.keys(pPayload);
  const lOldPayload = Object.assign({}, this.mParameters[pParameterId]);

  lPayloadKeys.forEach(function(key) {
    // If pPayload[key] is an object, iterate over its object keys and store associated values
    if (typeof pPayload[key] === 'object' && typeof lThis.mParameters[pParameterId][key] === 'object') {
      const lPayloadKeysKeys = Object.keys( pPayload[key]);
      lPayloadKeysKeys.forEach(function(key2) {
        lThis.mParameters[pParameterId][key][key2] = pPayload[key][key2];
      });
    } else {
      lThis.mParameters[pParameterId][key] = pPayload[key];
    }
  });

  // Update applicationParameters object
  this.mParameters = Object.assign({}, this.mParameters);

  // Update Application Payload: Update all instructions that were attached to this variable
  if (pPayload.hasOwnProperty('name')) {
    this.mApp.getAllInstructions().forEach((pInst) => {
      pInst._getInstructionModel().parameters
        .filter((pParam) => { return ( pParam.hasOwnProperty('acceptedVariableTypes') || pParam.name == 'axes' || pParam.name == 'expression' || pParam.name == 'conditionExpression' || pParam.name == 'conditions' || pParam.name == 'message' || pParam.name == 'variableName');})
        .forEach((pParam) => {
          let re; const lValue = pInst.getParameter(pParam.name);
          if ( typeof(lValue) === 'string' && ( pParam.name == 'expression' || pParam.name == 'conditionExpression' || pParam.hasOwnProperty('acceptedVariableTypes') || pParam.name == 'variableName' || pParam.name == 'message' ) ) {
            re = new RegExp(`\\b(${lOldPayload.name})\\b`, "g");
            const lUpdatedExp = lValue.replace(re, pPayload.name);
            !(lUpdatedExp === lValue) && pInst.setParameter(pParam.name, lUpdatedExp); //set parameter if instruction has changed
          }
          else if (pParam.name == 'conditions') {
            let lCondRequiresUpdate = false;
            lValue.forEach((lCond) => {
              if (lCond.instructionType != 'else') {
                re = new RegExp(`(${lOldPayload.name})\\b`, "g");
                const lUpdatedCond = lCond.conditionExpression.replace(re, pPayload.name);
                if (!(lUpdatedCond === lCond.conditionExpression)) {
                  lCondRequiresUpdate = true;
                  lCond.conditionExpression = lUpdatedCond;
                }
              }
            });
            lCondRequiresUpdate && pInst.setParameter(pParam.name, lValue); //set parameter if instruction has changed
          } else if (pParam.name == 'axes') {
            let lVarRequiresUpdate = false;
            lValue.forEach((lAxis) => {
              if (lAxis.distance && lAxis.distance == lOldPayload.name && typeof(lAxis.distance) === 'string') {
                lAxis.distance = pPayload.name;
                lVarRequiresUpdate = true;
              } else if (lAxis.position && lAxis.position == lOldPayload.name && typeof(lAxis.position) === 'string') {
                lAxis.position = pPayload.name;
                lVarRequiresUpdate = true;
              }
            });
            lVarRequiresUpdate && pInst.setParameter(pParam.name, lValue); //set parameter if instruction has changed
          }
        });
    });
    // Update UIB Widgets
    this.mApp.getUi() && this.mApp.getUi().listTabs().forEach(function (pTab) {
      const lWidgetsLinkedToVariables = pTab.listWidgets().filter(widget => widget.getType() == 'numericInput' || widget.getType() == 'dropdown' || widget.getType() == 'variableDisplay');
      lWidgetsLinkedToVariables.forEach(function (pWidget) {
        const lProps = pWidget.getProps();
        if (lProps.variable == lOldPayload.name) { lProps.variable = pPayload.name; }
      });
    });
  }

  this.setDirty();
};

/**
 * Get a unique parameter name. Used when add a new application parameter
 * @param {String} pParameterName - The default name for new application parameter
 */
ApplicationParameters.prototype.uniqueParameterName = function (pParameterName) {
  let lParameterName = pParameterName;
  let lCounter = 0;
  const lList = this.listParametersName();

  while (lList.includes(lParameterName)) {
    lCounter += 1;
    lParameterName = pParameterName + '_' + lCounter;
  }
  return lParameterName;
};

ApplicationParameters.prototype.listReferenceFrameNames = function() {
  const referenceFrames = this.getParameterByType('referenceFrame')?.default_value || [];
  return referenceFrames.map(obj => obj.name);
};

ApplicationParameters.prototype.uniqueReferenceFrameName = function() {
  const REFERENCE_FRAME_NAME = 'Reference Frame';

  return _uniqueName(REFERENCE_FRAME_NAME, this.listReferenceFrameNames());
};

const findByType = (pType) => (obj) => obj.type === pType;

ApplicationParameters.prototype.getParameterByType = function(pType) {
  return Object.values(this.mParameters).find(findByType(pType));
};

ApplicationParameters.prototype.hasParameterByType = function (pType) {
  return !!this.getParameterByType(pType);
};

Object.defineProperty(
  ApplicationParameters.prototype,
  'parameters',
  {
    get: function () {
      return this.mParameters;
    }
  }
);

Object.defineProperty(
  ApplicationParameters.prototype,
  'json',
  {
    get: function () {
      return this.toJson();
    }
  }
);
